import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { StrapiManagedUserRow } from '../../api/strapiUsers';
import { strapiUserRelationConnect } from '../../api/strapiUsers';
import { strapiListDynamicEntityPage, type DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { useChecklistSectionSearch } from '../../sampling/checklistSectionSearch';
import {
  formatBakuMutuLine,
  formatCompanyBranchLabel,
  formatJenisPengukuranLine,
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  strapiCreateWorkOrderBundle,
  strapiGetSamplingSchedule,
  strapiListSamplingPlansForSchedule,
  strapiPromoteScheduleToWorkOrder,
  strapiResolveSurveyTeamsForSamplingSchedule,
  type StrapiSamplingPlanCreateData,
  type StrapiSampleInformationRow,
  type StrapiWorkOrderBundleCreateData,
  type WorkOrderRomanQuarter,
} from '../../api/strapiCrm';
import { ChemParameterUjiCell } from '../../components/ChemParameterUjiCell';
import { MasterMultiCheckboxGroup } from '../../components/MasterMultiCheckboxGroup';
import { MetodaSamplingChemLabel, metodaSamplingPlainLabel } from '../../components/MetodaSamplingChemLabel';
import {
  testingDatesFromSamplingSchedule,
  WORK_ORDER_TESTING_DATES_HELPER,
} from '../SamplingWorkOrdersPage/workOrderShared';
import { useAuth } from '../../auth/AuthContext';
import { quotationStatusLabel, quotationUsesSampleGroups } from './quotationWorkflow';
import { canCreateWorkOrderFromSchedule, isAdminWorkOrder } from '../SamplingSchedulesPage/samplingScheduleAccess';
import { dynamicRelation } from '../../components/surveyScheduleCalendar/utils';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PAGE_SHELL_SCROLLABLE_TABS_SX, PageShell } from '../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../ui/CollapsibleKeteranganSplit';
import {
  useMasterEntityMiniListQuery,
  useParameterUjisByDocumentIdsQuery,
  useQuotationDetailQuery,
  useUsersQuery,
} from '../../query/hooks';
import { queryKeys } from '../../query/queryKeys';
import { defaultRomanMonthForDate, romanMonthMenuItems, type RomanMonth } from '../SamplingWorkOrdersPage/workOrderRomanMonth';
import { WORK_ORDER_STATUSES } from '../SamplingWorkOrdersPage/workOrderShared';
import { WorkOrderStatusMenuItemLabel, WorkOrderStatusTooltip } from '../SamplingWorkOrdersPage/WorkOrderStatusHover';
import {
  JENIS_PENGUKURAN_PERLENGKAPAN_API,
  samplingPlanPeralatanPreloadFromMapping,
  strapiListJenisPengukuranPerlengkapanByJenisIds,
  type JenisPengukuranPerlengkapanMapping,
} from '../../api/jenisPengukuranPerlengkapan';
import {
  applyParameterUjiMappingsToEmptyChecklist,
  applyParameterUjiMappingsToEmptySections,
  mappingAppliesToParameterIds,
  parameterDocumentIdsFromSampleLines,
  strapiListPeralatanToParameterUjiByParameterIds,
} from '../../api/peralatanToParameterUji';

import {
  filterSamplesByAirGroup,
  samplesHaveAirGroup,
  samplingPlanTabLabel,
  filterCaraSamplingReferensiByAirGroup,
  type SamplingPlanAirGroup,
} from '../SamplingWorkOrdersPage/samplingPlanAirGroup';
import {
  buildWorkOrderRouteHash,
  parseWorkOrderRouteHash,
  type WorkOrderRouteTab,
} from '../SamplingWorkOrdersPage/workOrderRouteHash';

const statuses = WORK_ORDER_STATUSES;

type TabKey = 'detail' | 'sampling_plan' | 'sampling_plan_air' | 'sampling_plan_non_air';

function tabKeyFromHash(hash: string): TabKey {
  const { tab } = parseWorkOrderRouteHash(hash);
  if (tab === 'samples') return 'sampling_plan';
  if (
    tab === 'detail' ||
    tab === 'sampling_plan' ||
    tab === 'sampling_plan_air' ||
    tab === 'sampling_plan_non_air'
  ) {
    return tab;
  }
  return 'detail';
}

function tabKeyToRouteTab(tab: TabKey): WorkOrderRouteTab {
  return tab;
}

type SamplingPlanChecklistState = {
  bidang_pengujian: DynamicEntityRow | null;
  tujuan_pengambilan: DynamicEntityRow[];
  pengawetan: DynamicEntityRow[];
  larutan_penjerap: DynamicEntityRow[];
  peralatan_pengambilan_contoh: DynamicEntityRow[];
  peralatan_pengukuran_lapangan: DynamicEntityRow[];
  peralatan_pendukung: DynamicEntityRow[];
  peralatan_k3: DynamicEntityRow[];
  cara_pengambilan_contoh: DynamicEntityRow[];
  pengendalian_mutu_lapangan: DynamicEntityRow[];
  pengamanan_transportasi_contoh: DynamicEntityRow[];
  administrasi_contoh: DynamicEntityRow[];
  peta_sketsa_lokasi_files: File[];
  petugas_pengambil_contoh: StrapiManagedUserRow | null;
  disahkan_oleh_manajer_teknis: StrapiManagedUserRow | null;
  metode_sampling: DynamicEntityRow[];
  sni_methods: DynamicEntityRow[];
  jumlah_wadah_contoh_uji_air: string;
  jumlah_wadah_contoh_uji_padatan: string;
};

const emptyChecklistState: SamplingPlanChecklistState = {
  bidang_pengujian: null,
  tujuan_pengambilan: [],
  pengawetan: [],
  larutan_penjerap: [],
  peralatan_pengambilan_contoh: [],
  peralatan_pengukuran_lapangan: [],
  peralatan_pendukung: [],
  peralatan_k3: [],
  cara_pengambilan_contoh: [],
  pengendalian_mutu_lapangan: [],
  pengamanan_transportasi_contoh: [],
  administrasi_contoh: [],
  peta_sketsa_lokasi_files: [],
  petugas_pengambil_contoh: null,
  disahkan_oleh_manajer_teknis: null,
  metode_sampling: [],
  sni_methods: [],
  jumlah_wadah_contoh_uji_air: '',
  jumlah_wadah_contoh_uji_padatan: '',
};

type ReferensiOptionsMode = 'exact' | 'prefix';

function useReferensiOptionsQuery(
  jwt: string | null,
  queryKey: string,
  kodeKategori: string,
  mode: ReferensiOptionsMode = 'exact',
) {
  return useQuery({
    queryKey: ['strapi', 'referensiOptions', jwt, queryKey, kodeKategori, mode],
    queryFn: async () => {
      const result = await strapiListDynamicEntityPage(jwt!, 'referensis', {
        populate: '*',
        page: 1,
        pageSize: 1000,
        ...(mode === 'exact'
          ? { exactFilters: { kode_kategori: kodeKategori } }
          : { containsFilters: { kode_kategori: kodeKategori } }),
      });
      return result.rows.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
    },
    enabled: Boolean(jwt),
  });
}

function relationConnect(row: DynamicEntityRow | null | undefined): { connect: string[] } | undefined {
  const documentId = String(row?.documentId ?? '').trim();
  return documentId ? { connect: [documentId] } : undefined;
}

function relationConnectMany(rows: DynamicEntityRow[]): { connect: string[] } | undefined {
  const ids = rows.map((row) => String(row.documentId ?? '').trim()).filter(Boolean);
  return ids.length ? { connect: ids } : undefined;
}

function trimToUndefined(v: string): string | undefined {
  const t = v.trim();
  return t ? t : undefined;
}

function rowString(row: DynamicEntityRow, key: string): string {
  return String(row[key] ?? '').trim();
}

function sniMethodOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [rowString(row, 'kode_sni'), rowString(row, 'judul')].filter(Boolean).join(' — ') || row.documentId;
}

function referensiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return (
    [rowString(row, 'kode_kategori'), rowString(row, 'kode'), rowString(row, 'deskripsi1')]
      .filter(Boolean)
      .join(' — ') || row.documentId
  );
}

function userOptionLabel(user: StrapiManagedUserRow | null | undefined): string {
  if (!user) return '';
  return (user.username?.trim() ? user.username : user.email) || user.id;
}

function lineJenisPengukuranDocumentId(line: StrapiSampleInformationRow): string {
  return String(line.jenis_pengukuran?.documentId ?? '').trim();
}

function peralatanFieldsEmpty(state: SamplingPlanChecklistState): boolean {
  return (
    !state.peralatan_pengambilan_contoh.length &&
    !state.peralatan_pengukuran_lapangan.length &&
    !state.peralatan_pendukung.length &&
    !state.peralatan_k3.length
  );
}

function validateMandatoryChecklist(
  state: SamplingPlanChecklistState,
  groupLabel: string,
): string | null {
  const missing: string[] = [];
  if (state.tujuan_pengambilan.length === 0) missing.push('Tujuan pengambilan contoh');
  if (state.metode_sampling.length === 0) missing.push('Parameter');
  if (peralatanFieldsEmpty(state)) missing.push('Peralatan (minimal satu jenis)');
  if (state.cara_pengambilan_contoh.length === 0) missing.push('Cara pengambilan contoh');
  if (!state.petugas_pengambil_contoh) missing.push('Petugas pengambil contoh');
  if (missing.length === 0) return null;
  return `${groupLabel}: lengkapi ${missing.join(', ')}.`;
}

function applyPerlengkapanMappingToChecklist(
  state: SamplingPlanChecklistState,
  mapping: JenisPengukuranPerlengkapanMapping | undefined,
): SamplingPlanChecklistState {
  if (!mapping || !peralatanFieldsEmpty(state)) return state;
  const preload = samplingPlanPeralatanPreloadFromMapping(mapping);
  return {
    ...state,
    peralatan_pengambilan_contoh: preload.peralatan_pengambilan_contoh ?? state.peralatan_pengambilan_contoh,
    peralatan_pengukuran_lapangan: preload.peralatan_pengukuran_lapangan ?? state.peralatan_pengukuran_lapangan,
    peralatan_pendukung: preload.peralatan_pendukung ?? state.peralatan_pendukung,
    peralatan_k3: preload.peralatan_k3 ?? state.peralatan_k3,
  };
}

function buildSamplingPlanChecklistPayload(checklist: SamplingPlanChecklistState): Partial<StrapiSamplingPlanCreateData> {
  return {
    ...(relationConnect(checklist.bidang_pengujian) ? { bidang_pengujian: relationConnect(checklist.bidang_pengujian) } : {}),
    ...(relationConnectMany(checklist.tujuan_pengambilan) ? { tujuan_pengambilan: relationConnectMany(checklist.tujuan_pengambilan) } : {}),
    ...(relationConnectMany(checklist.pengawetan) ? { pengawetan: relationConnectMany(checklist.pengawetan) } : {}),
    ...(relationConnectMany(checklist.larutan_penjerap) ? { larutan_penjerap: relationConnectMany(checklist.larutan_penjerap) } : {}),
    ...(relationConnectMany(checklist.peralatan_pengambilan_contoh)
      ? { peralatan_pengambilan_contoh: relationConnectMany(checklist.peralatan_pengambilan_contoh) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_pengukuran_lapangan)
      ? { peralatan_pengukuran_lapangan: relationConnectMany(checklist.peralatan_pengukuran_lapangan) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_pendukung)
      ? { peralatan_pendukung: relationConnectMany(checklist.peralatan_pendukung) }
      : {}),
    ...(relationConnectMany(checklist.peralatan_k3) ? { peralatan_k3: relationConnectMany(checklist.peralatan_k3) } : {}),
    ...(relationConnectMany(checklist.cara_pengambilan_contoh)
      ? { cara_pengambilan_contoh: relationConnectMany(checklist.cara_pengambilan_contoh) }
      : {}),
    ...(relationConnectMany(checklist.pengendalian_mutu_lapangan)
      ? { pengendalian_mutu_lapangan: relationConnectMany(checklist.pengendalian_mutu_lapangan) }
      : {}),
    ...(relationConnectMany(checklist.pengamanan_transportasi_contoh)
      ? { pengamanan_transportasi_contoh: relationConnectMany(checklist.pengamanan_transportasi_contoh) }
      : {}),
    ...(relationConnectMany(checklist.administrasi_contoh)
      ? { administrasi_contoh: relationConnectMany(checklist.administrasi_contoh) }
      : {}),
    ...(checklist.petugas_pengambil_contoh
      ? { petugas_pengambil_contoh: strapiUserRelationConnect(checklist.petugas_pengambil_contoh) }
      : {}),
    ...(checklist.disahkan_oleh_manajer_teknis
      ? { disahkan_oleh_manajer_teknis: strapiUserRelationConnect(checklist.disahkan_oleh_manajer_teknis) }
      : {}),
    ...(relationConnectMany(checklist.metode_sampling) ? { metode_sampling: relationConnectMany(checklist.metode_sampling) } : {}),
    ...(relationConnectMany(checklist.sni_methods) ? { sni_methods: relationConnectMany(checklist.sni_methods) } : {}),
    ...(trimToUndefined(checklist.jumlah_wadah_contoh_uji_air)
      ? { jumlah_wadah_contoh_uji_air: trimToUndefined(checklist.jumlah_wadah_contoh_uji_air) }
      : {}),
    ...(trimToUndefined(checklist.jumlah_wadah_contoh_uji_padatan)
      ? { jumlah_wadah_contoh_uji_padatan: trimToUndefined(checklist.jumlah_wadah_contoh_uji_padatan) }
      : {}),
    ...(checklist.peta_sketsa_lokasi_files.length
      ? { peta_sketsa_lokasi_files: checklist.peta_sketsa_lokasi_files }
      : {}),
  };
}

type MasterMultiAutocompleteProps = {
  label: string;
  options: DynamicEntityRow[];
  value: DynamicEntityRow[];
  onChange: (rows: DynamicEntityRow[]) => void;
  getOptionLabel: (row: DynamicEntityRow | null | undefined) => string;
  loading?: boolean;
  onSearchMiss?: (query: string) => Promise<DynamicEntityRow[]>;
  'data-testid'?: string;
};

function MasterMultiAutocomplete(props: MasterMultiAutocompleteProps) {
  return <MasterMultiCheckboxGroup {...props} />;
}

export function QuotationWorkOrderCreateRoute() {
  const { jwt, user } = useAuth();
  const sectionSearch = useChecklistSectionSearch(jwt);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { documentId: rawId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('scheduleId')?.trim() || null;
  const documentId = rawId ? decodeURIComponent(rawId) : null;

  const detailQ = useQuotationDetailQuery(jwt, documentId);
  const scheduleQ = useQuery({
    queryKey: ['strapi', 'samplingSchedule', jwt, scheduleId],
    queryFn: () => strapiGetSamplingSchedule(jwt!, scheduleId!),
    enabled: Boolean(jwt && scheduleId),
  });
  const schedulePlansQ = useQuery({
    queryKey: ['strapi', 'samplingPlansForSchedule', jwt, scheduleId],
    queryFn: () => strapiListSamplingPlansForSchedule(jwt!, scheduleId!),
    enabled: Boolean(jwt && scheduleId),
  });
  const scheduleTeamsQ = useQuery({
    queryKey: [
      'strapi',
      'surveyTeamsBySchedule',
      jwt,
      scheduleId,
      scheduleQ.data?.work_order?.documentId ?? '',
    ],
    queryFn: () =>
      strapiResolveSurveyTeamsForSamplingSchedule(
        jwt!,
        scheduleId!,
        scheduleQ.data?.work_order?.documentId ?? null,
      ),
    enabled: Boolean(jwt && scheduleId && scheduleQ.isSuccess),
  });
  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'active' });
  const parameterMetodaQ = useMasterEntityMiniListQuery(jwt, 'parameter-uji-metodas', Boolean(jwt));
  const sniMethodQ = useMasterEntityMiniListQuery(jwt, 'sni-methods', Boolean(jwt));
  const tujuanPengambilanQ = useReferensiOptionsQuery(jwt, 'tujuan_pengambilan', 'TUJUAN_SAMPLING');
  const pengawetanQ = useReferensiOptionsQuery(jwt, 'pengawetan', 'PENGAWETAN');
  const larutanPenjerapQ = useReferensiOptionsQuery(jwt, 'larutan_penjerap', 'LARUTAN_PENJERAP');
  const peralatanPengambilanContohQ = useReferensiOptionsQuery(
    jwt,
    'peralatan_pengambilan_contoh',
    'PERALATAN_SAMPLING',
    'prefix',
  );
  const peralatanPengukuranLapanganQ = useReferensiOptionsQuery(
    jwt,
    'peralatan_pengukuran_lapangan',
    'PERALATAN_UKUR_LAP',
  );
  const peralatanPendukungQ = useReferensiOptionsQuery(jwt, 'peralatan_pendukung', 'PERALATAN_PENDUKUNG');
  const peralatanK3Q = useReferensiOptionsQuery(jwt, 'peralatan_k3', 'PERALATAN_K3');
  const caraPengambilanContohQ = useReferensiOptionsQuery(
    jwt,
    'cara_pengambilan_contoh',
    'CARA_SAMPLING_',
    'prefix',
  );
  const pengendalianMutuLapanganQ = useReferensiOptionsQuery(
    jwt,
    'pengendalian_mutu_lapangan',
    'PENGENDALIAN_MUTU',
    'prefix',
  );
  const pengamananTransportasiContohQ = useReferensiOptionsQuery(
    jwt,
    'pengamanan_transportasi_contoh',
    'PENGAMANAN_SAMPLING',
  );
  const administrasiContohQ = useReferensiOptionsQuery(jwt, 'administrasi_contoh', 'ADMINISTRASI_SAMPEL');
  const createMut = useMutation({
    mutationFn: (data: Parameters<typeof strapiCreateWorkOrderBundle>[1]) => {
      if (!jwt) throw new Error('Sesi tidak valid atau Anda belum masuk. Silakan login kembali.');
      return strapiCreateWorkOrderBundle(jwt, data);
    },
  });

  const defaultDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultYear = useMemo(() => new Date().getFullYear(), []);

  const tab = useMemo(() => tabKeyFromHash(location.hash), [location.hash]);

  function goTab(nextTab: TabKey) {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: buildWorkOrderRouteHash(tabKeyToRouteTab(nextTab)),
      },
      { replace: true },
    );
  }

  const [workOrderDate, setWorkOrderDate] = useState(defaultDate);
  const [status, setStatus] = useState<(typeof statuses)[number]>('draft');
  const [romanQuarter, setRomanQuarter] = useState<RomanMonth>(() => defaultRomanMonthForDate());
  const [calendarYear, setCalendarYear] = useState<number>(defaultYear);
  const [testingStart, setTestingStart] = useState('');
  const [testingEnd, setTestingEnd] = useState('');
  const [samplingChecklistByAirGroup, setSamplingChecklistByAirGroup] = useState<
    Partial<Record<SamplingPlanAirGroup, SamplingPlanChecklistState>>
  >({});
  const [activeChecklistGroup, setActiveChecklistGroup] = useState<SamplingPlanAirGroup | null>(null);
  const [checklistDraft, setChecklistDraft] = useState<SamplingPlanChecklistState>(emptyChecklistState);

  const [sampleSubmitError, setSampleSubmitError] = useState<string | null>(null);
  /** Pesan validasi sisi klien setelah klik Simpan (jwt, tab kosong, dll.) */
  const [formValidationError, setFormValidationError] = useState<string | null>(null);

  useDocumentTitle(appDocumentTitle('Buat work order'));

  const picOptions = useMemo(() => {
    const rows = usersQ.data?.rows ?? [];
    return rows.filter((u) => u.documentId != null || u.numericId != null);
  }, [usersQ.data?.rows]);

  const metodeSamplingOptions = useMemo(
    () => [...(parameterMetodaQ.data ?? [])].sort((a, b) => metodaSamplingPlainLabel(a).localeCompare(metodaSamplingPlainLabel(b), 'id')),
    [parameterMetodaQ.data],
  );
  const sniMethodOptions = useMemo(
    () => [...(sniMethodQ.data ?? [])].sort((a, b) => sniMethodOptionLabel(a).localeCompare(sniMethodOptionLabel(b), 'id')),
    [sniMethodQ.data],
  );
  const referensiOptionsByField = {
    tujuan_pengambilan: tujuanPengambilanQ.data ?? [],
    pengawetan: pengawetanQ.data ?? [],
    larutan_penjerap: larutanPenjerapQ.data ?? [],
    peralatan_pengambilan_contoh: peralatanPengambilanContohQ.data ?? [],
    peralatan_pengukuran_lapangan: peralatanPengukuranLapanganQ.data ?? [],
    peralatan_pendukung: peralatanPendukungQ.data ?? [],
    peralatan_k3: peralatanK3Q.data ?? [],
    cara_pengambilan_contoh: caraPengambilanContohQ.data ?? [],
    pengendalian_mutu_lapangan: pengendalianMutuLapanganQ.data ?? [],
    pengamanan_transportasi_contoh: pengamananTransportasiContohQ.data ?? [],
    administrasi_contoh: administrasiContohQ.data ?? [],
  };

  const romanOptions = useMemo(() => romanMonthMenuItems(), []);

  const usesGroups = detailQ.data ? quotationUsesSampleGroups(detailQ.data) : false;
  const scheduledSampleLineDocIds = useMemo(() => {
    if (!scheduleId || !usesGroups) return null;
    const ids = new Set<string>();
    for (const plan of schedulePlansQ.data ?? []) {
      const line = dynamicRelation(plan, 'quotation_sample_information');
      const docId = String(line?.documentId ?? '').trim();
      if (docId) ids.add(docId);
    }
    return ids;
  }, [scheduleId, usesGroups, schedulePlansQ.data]);

  const visibleSampleLines = useMemo(() => {
    const all = detailQ.data?.sample_informations ?? [];
    if (!scheduleId || !usesGroups) return all;
    if (!scheduledSampleLineDocIds) return all;
    return all.filter((line) => line.documentId && scheduledSampleLineDocIds.has(line.documentId));
  }, [detailQ.data?.sample_informations, scheduleId, usesGroups, scheduledSampleLineDocIds]);

  const airSampleLines = useMemo(
    () => filterSamplesByAirGroup(visibleSampleLines, 'air'),
    [visibleSampleLines],
  );
  const nonAirSampleLines = useMemo(
    () => filterSamplesByAirGroup(visibleSampleLines, 'non_air'),
    [visibleSampleLines],
  );
  const hasAirSamples = samplesHaveAirGroup(visibleSampleLines, 'air');
  const hasNonAirSamples = samplesHaveAirGroup(visibleSampleLines, 'non_air');

  useEffect(() => {
    if (!detailQ.data) return;
    if (scheduleId && usesGroups && schedulePlansQ.isPending) return;
    if (tab === 'sampling_plan_air' || tab === 'sampling_plan_non_air') {
      goTab('sampling_plan');
    }
  }, [
    tab,
    hasAirSamples,
    hasNonAirSamples,
    detailQ.data,
    scheduleId,
    usesGroups,
    schedulePlansQ.isPending,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (tab !== 'sampling_plan_air' && tab !== 'sampling_plan_non_air') return;
    const group: SamplingPlanAirGroup = tab === 'sampling_plan_air' ? 'air' : 'non_air';
    const lines = group === 'air' ? airSampleLines : nonAirSampleLines;
    if (!lines.length) return;
    if (activeChecklistGroup === group) return;
    setActiveChecklistGroup(group);
    setChecklistDraft(checklistForAirGroup(group));
  }, [tab, airSampleLines, nonAirSampleLines, activeChecklistGroup, samplingChecklistByAirGroup]);

  /** Autosave draft ke state per group (ganti tombol Simpan checklist). */
  useEffect(() => {
    if (!activeChecklistGroup) return;
    if (tab !== 'sampling_plan_air' && tab !== 'sampling_plan_non_air') return;
    setSamplingChecklistByAirGroup((prev) => ({
      ...prev,
      [activeChecklistGroup]: {
        ...checklistDraft,
        bidang_pengujian: null,
      },
    }));
  }, [checklistDraft, activeChecklistGroup, tab]);

  const uniqueJenisPengukuranDocIds = useMemo(() => {
    const set = new Set<string>();
    for (const line of visibleSampleLines) {
      const id = lineJenisPengukuranDocumentId(line);
      if (id) set.add(id);
    }
    return [...set];
  }, [visibleSampleLines]);

  const jenisPerlengkapanMappingsQ = useQuery({
    queryKey: ['strapi', JENIS_PENGUKURAN_PERLENGKAPAN_API, jwt, uniqueJenisPengukuranDocIds.join(',')],
    queryFn: () => strapiListJenisPengukuranPerlengkapanByJenisIds(jwt!, uniqueJenisPengukuranDocIds),
    enabled: Boolean(jwt && uniqueJenisPengukuranDocIds.length),
  });

  const quotationLineCount = visibleSampleLines.length;
  const quotationLinesOk = quotationLineCount > 0;

  const allParameterUjiDocIds = useMemo(() => {
    const set = new Set<string>();
    for (const line of visibleSampleLines) {
      for (const t of parseStoredParameterUjiDocumentIds(line.parameter_uji ?? '')) {
        if (isLikelyStrapiDocumentId(t)) set.add(t.trim());
      }
    }
    return [...set];
  }, [visibleSampleLines]);
  const parameterChecklistIds = useMemo(
    () => parameterDocumentIdsFromSampleLines(visibleSampleLines),
    [visibleSampleLines],
  );
  const parameterChecklistMappingQ = useQuery({
    queryKey: ['strapi', 'peralatan-to-parameter-ujis', jwt, parameterChecklistIds.join(',')],
    queryFn: () => strapiListPeralatanToParameterUjiByParameterIds(jwt!, parameterChecklistIds),
    enabled: Boolean(jwt && parameterChecklistIds.length),
  });
  const parameterMappingApplyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeChecklistGroup || !parameterChecklistMappingQ.isSuccess) return;
    const lines = activeChecklistGroup === 'air' ? airSampleLines : nonAirSampleLines;
    const ids = parameterDocumentIdsFromSampleLines(lines);
    const key = `${activeChecklistGroup}|${ids.join(',')}`;
    if (parameterMappingApplyKeyRef.current === key) return;
    parameterMappingApplyKeyRef.current = key;
    const groupMappings = (parameterChecklistMappingQ.data ?? []).filter((row) =>
      mappingAppliesToParameterIds(row, ids),
    );
    setChecklistDraft((prev) => applyParameterUjiMappingsToEmptySections(prev, groupMappings));
  }, [
    activeChecklistGroup,
    airSampleLines,
    nonAirSampleLines,
    parameterChecklistMappingQ.isSuccess,
    parameterChecklistMappingQ.data,
  ]);
  const parameterUjisLookupQ = useParameterUjisByDocumentIdsQuery(jwt, allParameterUjiDocIds);
  const parameterUjisMap = parameterUjisLookupQ.data ?? new Map();

  const yearForValidation = Number(calendarYear);
  const calendarYearOk =
    Number.isFinite(yearForValidation) &&
    yearForValidation >= 1900 &&
    yearForValidation <= 2100;
  const detailOk = Boolean(workOrderDate?.trim() && String(romanQuarter ?? '').trim() && calendarYearOk);

  /** Tahun aman untuk API (setelah validasi detailOk) */
  const calendarYearForApi = Math.round(yearForValidation);

  const scheduleWorkOrderReady = useMemo(() => {
    if (!scheduleQ.data) return false;
    return canCreateWorkOrderFromSchedule(scheduleQ.data, user);
  }, [scheduleQ.data, user]);

  useEffect(() => {
    if (!scheduleQ.data) return;
    const { start, end } = testingDatesFromSamplingSchedule(scheduleQ.data);
    setTestingStart(start);
    setTestingEnd(end);
  }, [scheduleQ.data]);

  function checklistForAirGroup(group: SamplingPlanAirGroup): SamplingPlanChecklistState {
    const saved =
      activeChecklistGroup === group
        ? checklistDraft
        : samplingChecklistByAirGroup[group];
    const base: SamplingPlanChecklistState = {
      ...emptyChecklistState,
      ...(saved ?? {}),
      bidang_pengujian: null,
    };
    const lines = group === 'air' ? airSampleLines : nonAirSampleLines;
    const firstJenisId = lines.map(lineJenisPengukuranDocumentId).find(Boolean);
    const mapping = firstJenisId ? jenisPerlengkapanMappingsQ.data?.get(firstJenisId) : undefined;
    const parameterMappings = (parameterChecklistMappingQ.data ?? []).filter((row) =>
      mappingAppliesToParameterIds(row, parameterDocumentIdsFromSampleLines(lines)),
    );
    const withParameter = applyParameterUjiMappingsToEmptyChecklist(base, parameterMappings);
    return applyPerlengkapanMappingToChecklist(withParameter, mapping);
  }

  const airChecklistError = hasAirSamples
    ? validateMandatoryChecklist(checklistForAirGroup('air'), samplingPlanTabLabel('air'))
    : null;
  const nonAirChecklistError = hasNonAirSamples
    ? validateMandatoryChecklist(checklistForAirGroup('non_air'), samplingPlanTabLabel('non_air'))
    : null;
  const checklistOk = !airChecklistError && !nonAirChecklistError;

  /** Validasi bisnis (tanpa status mutation); tombol tetap aktif sampai klik, lalu dicek saat submit. */
  const formReadyForSubmit =
    detailOk &&
    quotationLinesOk;

  const submitBlockedHint = useMemo(() => {
    if (formReadyForSubmit || createMut.isPending) return null;
    if (!detailOk) return 'Lengkapi tab Detail (tanggal, status, bulan Romawi, tahun).';
    if (!quotationLinesOk) return 'Quotation harus memiliki minimal satu baris sampel.';
    return null;
  }, [
    formReadyForSubmit,
    createMut.isPending,
    detailOk,
    quotationLinesOk,
  ]);

  useEffect(() => {
    if (formReadyForSubmit) setFormValidationError(null);
  }, [formReadyForSubmit]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormValidationError(null);
    setSampleSubmitError(null);

    if (!jwt) {
      setFormValidationError('Sesi tidak valid atau Anda belum masuk. Silakan login kembali.');
      return;
    }
    if (!documentId) {
      setFormValidationError('ID quotation tidak valid.');
      return;
    }
    if (!formReadyForSubmit) {
      setFormValidationError(
        submitBlockedHint ??
          'Lengkapi tab Detail dan pastikan quotation memiliki baris sampel.',
      );
      return;
    }

    const quotationRow = detailQ.data;
    if (!quotationRow) {
      setFormValidationError('Data quotation belum dimuat.');
      return;
    }

    createMut.reset();

    try {
      // Plan per baris (survey/BA) — tanpa checklist; checklist ada di plan air_group.
      const samplingPlans: StrapiWorkOrderBundleCreateData['sampling_plans'] = [
        ...visibleSampleLines.map((line, i) => {
          const sc =
            line.sample_point_count != null && Number.isFinite(Number(line.sample_point_count))
              ? Math.max(1, Number(line.sample_point_count))
              : 1;
          return {
            ...(line.documentId ? { quotation_sample_information: { connect: [line.documentId] } } : {}),
            line_index: i,
            sample_point_count: sc,
            progress_status: 'draft' as const,
            ...(line.jenis_pengukuran?.documentId
              ? { bidang_pengujian: { connect: [line.jenis_pengukuran.documentId] } }
              : {}),
          };
        }),
      ];

      if (scheduleId) {
        const wo = await strapiPromoteScheduleToWorkOrder(jwt, scheduleId, {
          work_order_date: workOrderDate,
          progress_status: status,
          roman_quarter: romanQuarter as WorkOrderRomanQuarter,
          calendar_year: calendarYearForApi,
        });
        await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, documentId) });
        await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
        await qc.invalidateQueries({ queryKey: ['strapi', 'samplingSchedule', jwt, scheduleId] });
        navigate(`/sampling/work-order/${encodeURIComponent(wo.documentId)}/view`);
        return;
      }

      const wo = await createMut.mutateAsync({
        quotation_document_id: documentId,
        work_order: {
          quotation: { connect: [documentId] },
          work_order_date: workOrderDate,
          progress_status: status,
          roman_quarter: romanQuarter as WorkOrderRomanQuarter,
          calendar_year: calendarYearForApi,
        },
        sampling_plans: samplingPlans,
      });
      await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
      await qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, documentId) });
      await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
      navigate(`/sampling/work-order/${encodeURIComponent(wo.documentId)}/view`);
    } catch {
      /* createMut mengisi isError; pesan tampil di Alert */
    }
  }

  if (!documentId) {
    return (
      <PageShell title="Buat work order">
        <Alert severity="warning">ID quotation tidak valid.</Alert>
        <Button component={RouterLink} to="/quotations" sx={{ mt: 2 }}>
          Kembali ke daftar
        </Button>
      </PageShell>
    );
  }

  if (detailQ.isPending || (scheduleId && (scheduleQ.isPending || schedulePlansQ.isPending || scheduleTeamsQ.isPending))) {
    return (
      <PageShell fill title="Buat work order">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </PageShell>
    );
  }

  if (detailQ.isError) {
    return (
      <PageShell title="Buat work order">
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat quotation.'}
        </Alert>
        <Button component={RouterLink} to="/quotations">
          Kembali ke daftar
        </Button>
      </PageShell>
    );
  }

  const q = detailQ.data!;

  if (q.progress_status !== 'customer_approved') {
    return (
      <PageShell title="Buat work order">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Work order hanya dapat dibuat setelah quotation disetujui pelanggan (status:{' '}
          {quotationStatusLabel('customer_approved')}).
          Status saat ini: {quotationStatusLabel(q.progress_status)}.
        </Alert>
        <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
          Kembali ke detail quotation
        </Button>
      </PageShell>
    );
  }

  if (!scheduleId && q.sample_pickup_method === 'diantar') {
    return (
      <PageShell title="Buat work order">
        <Alert severity="info" sx={{ mb: 2 }}>
          Work order metode diantar dibuat otomatis setelah pelanggan setuju dan masuk ke admin
          penerima sample. Nomor job terbit saat sampel diterima.
        </Alert>
        <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
          Kembali ke detail quotation
        </Button>
      </PageShell>
    );
  }

  if (!scheduleId) {
    if (!isAdminWorkOrder(user)) {
      return (
        <PageShell title="Buat work order">
          <Alert severity="warning" sx={{ mb: 2 }}>
            Hanya Admin Work Order yang dapat membuat work order langsung dari quotation.
          </Alert>
          <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
            Kembali ke detail quotation
          </Button>
        </PageShell>
      );
    }
    if (q.sample_pickup_method !== 'diantar') {
      return (
        <PageShell title="Buat work order">
          <Alert severity="warning" sx={{ mb: 2 }}>
            Quotation dengan metode pengambilan sampel <strong>sampling</strong> harus dibuat work
            order melalui penjadwalan sampling, bukan dari halaman ini.
          </Alert>
          <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
            Kembali ke detail quotation
          </Button>
        </PageShell>
      );
    }
  }

  if (
    scheduleId &&
    scheduleQ.data &&
    !scheduleWorkOrderReady &&
    !scheduleQ.data.work_order?.documentId
  ) {
    return (
      <PageShell title="Buat work order">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Work order hanya dapat dibuat oleh Admin WO setelah tanggal penjadwalan disimpan.
        </Alert>
        <Button component={RouterLink} to={`/sampling/schedules/${encodeURIComponent(scheduleId)}`}>
          Kembali ke penjadwalan
        </Button>
      </PageShell>
    );
  }

  if (scheduleId && !isAdminWorkOrder(user)) {
    return (
      <PageShell title="Buat work order">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Hanya Admin Work Order yang dapat membuat work order dari halaman ini.
        </Alert>
        <Button component={RouterLink} to={`/sampling/schedules/${encodeURIComponent(scheduleId)}`}>
          Kembali ke penjadwalan
        </Button>
      </PageShell>
    );
  }

  if (!scheduleId && q.work_order_created) {
    return (
      <PageShell title="Buat work order">
        <Alert severity="info" sx={{ mb: 2 }}>
          Work order untuk quotation ini sudah pernah dibuat. Buat quotation baru jika perlu penambahan
          sampel.
        </Alert>
        <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
          Kembali ke detail quotation
        </Button>
      </PageShell>
    );
  }

  const quotationSampleLines = visibleSampleLines;

  function renderSamplingPlanTable(lines: StrapiSampleInformationRow[]) {
    if (lines.length === 0) {
      return (
        <Alert severity="warning">
          Quotation tidak memiliki baris sampel. Lengkapi quotation sebelum membuat work order.
        </Alert>
      );
    }
    return (
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { verticalAlign: 'middle', py: 1 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 56 }}>#</TableCell>
            <TableCell sx={{ width: '22%' }}>Jenis pengukuran</TableCell>
            <TableCell sx={{ width: '24%' }}>Baku mutu</TableCell>
            <TableCell>Parameter uji</TableCell>
            <TableCell align="right" sx={{ width: 112 }}>
              Jumlah titik
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line, idx) => (
              <TableRow key={line.documentId || `line-${idx}`}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{formatJenisPengukuranLine(line.jenis_pengukuran)}</TableCell>
                <TableCell>{formatBakuMutuLine(line.baku_mutu)}</TableCell>
                <TableCell>
                  <Typography variant="body2" component="div">
                    <ChemParameterUjiCell
                      raw={line.parameter_uji}
                      items={line.parameter_ujis}
                      puLookup={parameterUjisMap}
                    />
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {line.sample_point_count != null ? String(line.sample_point_count) : '—'}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    );
  }

  function renderInlineChecklistForm(lines: StrapiSampleInformationRow[], group: SamplingPlanAirGroup) {
    if (lines.length === 0) {
      return (
        <Alert severity="info">
          Tidak ada jenis pengukuran untuk group {group === 'air' ? 'air/padatan' : 'udara/emisi/lingkungan'} pada
          quotation ini.
        </Alert>
      );
    }
    const groupJenisLabels = lines
      .map((line) => formatJenisPengukuranLine(line.jenis_pengukuran))
      .filter(Boolean);
    return (
      <Stack spacing={2} data-testid={`wo-create-checklist-panel-${group}`}>
        <Typography variant="body2" color="text.secondary">
          Satu form checklist untuk seluruh jenis di group ini. Checklist ikut tersimpan saat Anda klik Simpan work
          order di footer. Field tujuan, parameter, peralatan, cara pengambilan, dan petugas wajib diisi.
        </Typography>
        <Stack spacing={2} sx={{ width: '100%' }}>
          <MasterMultiAutocomplete
            label="Tujuan pengambilan contoh"
            options={referensiOptionsByField.tujuan_pengambilan}
            value={checklistDraft.tujuan_pengambilan}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, tujuan_pengambilan: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={tujuanPengambilanQ.isPending}
            onSearchMiss={sectionSearch?.tujuan}
            data-testid={`wo-create-checklist-${group}-tujuan`}
          />
          <Alert severity="info">
            Jenis di group ini: <strong>{groupJenisLabels.join(', ') || '—'}</strong>
          </Alert>
          <MasterMultiCheckboxGroup
            label="Metode sampling"
            options={sniMethodOptions}
            value={checklistDraft.sni_methods}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, sni_methods: rows }))}
            getOptionLabel={sniMethodOptionLabel}
            loading={sniMethodQ.isPending}
            onSearchMiss={sectionSearch?.sni}
            data-testid={`wo-create-checklist-${group}-sni`}
          />
          <MasterMultiCheckboxGroup
            label="Parameter"
            options={metodeSamplingOptions}
            value={checklistDraft.metode_sampling}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, metode_sampling: rows }))}
            getOptionLabel={metodaSamplingPlainLabel}
            renderOptionLabel={(row) => <MetodaSamplingChemLabel row={row} />}
            loading={parameterMetodaQ.isPending}
            onSearchMiss={sectionSearch?.parameter}
            data-testid={`wo-create-checklist-${group}-metode`}
          />
          <MasterMultiAutocomplete
            label="Larutan penyerap"
            options={referensiOptionsByField.larutan_penjerap}
            value={checklistDraft.larutan_penjerap}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, larutan_penjerap: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={larutanPenjerapQ.isPending}
            onSearchMiss={sectionSearch?.larutan}
            data-testid={`wo-create-checklist-${group}-larutan`}
          />
          <MasterMultiAutocomplete
            label="Peralatan pengambil contoh"
            options={referensiOptionsByField.peralatan_pengambilan_contoh}
            value={checklistDraft.peralatan_pengambilan_contoh}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, peralatan_pengambilan_contoh: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={peralatanPengambilanContohQ.isPending}
            onSearchMiss={sectionSearch?.peralatanPengambilan}
            data-testid={`wo-create-checklist-${group}-peralatan-pengambilan`}
          />
          <MasterMultiAutocomplete
            label="Peralatan pengukuran lapangan"
            options={referensiOptionsByField.peralatan_pengukuran_lapangan}
            value={checklistDraft.peralatan_pengukuran_lapangan}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, peralatan_pengukuran_lapangan: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={peralatanPengukuranLapanganQ.isPending}
            onSearchMiss={sectionSearch?.peralatanUkur}
            data-testid={`wo-create-checklist-${group}-peralatan-ukur`}
          />
          <MasterMultiAutocomplete
            label="Peralatan pendukung"
            options={referensiOptionsByField.peralatan_pendukung}
            value={checklistDraft.peralatan_pendukung}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, peralatan_pendukung: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={peralatanPendukungQ.isPending}
            onSearchMiss={sectionSearch?.peralatanPendukung}
            data-testid={`wo-create-checklist-${group}-peralatan-pendukung`}
          />
          <MasterMultiAutocomplete
            label="Peralatan K-3"
            options={referensiOptionsByField.peralatan_k3}
            value={checklistDraft.peralatan_k3}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, peralatan_k3: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={peralatanK3Q.isPending}
            onSearchMiss={sectionSearch?.peralatanK3}
            data-testid={`wo-create-checklist-${group}-peralatan-k3`}
          />
          <MasterMultiAutocomplete
            label="Cara pengambilan contoh"
            options={filterCaraSamplingReferensiByAirGroup(
              referensiOptionsByField.cara_pengambilan_contoh,
              group,
            )}
            value={checklistDraft.cara_pengambilan_contoh}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, cara_pengambilan_contoh: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={caraPengambilanContohQ.isPending}
            onSearchMiss={
              sectionSearch
                ? async (query) => filterCaraSamplingReferensiByAirGroup(await sectionSearch.cara(query), group)
                : undefined
            }
            data-testid={`wo-create-checklist-${group}-cara`}
          />
          <MasterMultiAutocomplete
            label="Pengendalian mutu lapangan"
            options={referensiOptionsByField.pengendalian_mutu_lapangan}
            value={checklistDraft.pengendalian_mutu_lapangan}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, pengendalian_mutu_lapangan: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={pengendalianMutuLapanganQ.isPending}
            onSearchMiss={sectionSearch?.mutu}
            data-testid={`wo-create-checklist-${group}-mutu`}
          />
          <MasterMultiAutocomplete
            label="Pengamanan dan transportasi contoh"
            options={referensiOptionsByField.pengamanan_transportasi_contoh}
            value={checklistDraft.pengamanan_transportasi_contoh}
            onChange={(rows) =>
              setChecklistDraft((prev) => ({ ...prev, pengamanan_transportasi_contoh: rows }))
            }
            getOptionLabel={referensiOptionLabel}
            loading={pengamananTransportasiContohQ.isPending}
            onSearchMiss={sectionSearch?.transport}
            data-testid={`wo-create-checklist-${group}-transport`}
          />
          <MasterMultiAutocomplete
            label="Administrasi contoh"
            options={referensiOptionsByField.administrasi_contoh}
            value={checklistDraft.administrasi_contoh}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, administrasi_contoh: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={administrasiContohQ.isPending}
            onSearchMiss={sectionSearch?.administrasi}
            data-testid={`wo-create-checklist-${group}-administrasi`}
          />
          <MasterMultiAutocomplete
            label="Pengawetan (air)"
            options={referensiOptionsByField.pengawetan}
            value={checklistDraft.pengawetan}
            onChange={(rows) => setChecklistDraft((prev) => ({ ...prev, pengawetan: rows }))}
            getOptionLabel={referensiOptionLabel}
            loading={pengawetanQ.isPending}
            onSearchMiss={sectionSearch?.pengawetan}
            data-testid={`wo-create-checklist-${group}-pengawetan`}
          />
          <Autocomplete
            options={picOptions}
            value={checklistDraft.petugas_pengambil_contoh}
            onChange={(_, row) => setChecklistDraft((prev) => ({ ...prev, petugas_pengambil_contoh: row }))}
            loading={usersQ.isPending}
            getOptionLabel={userOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Created by petugas pengambil contoh"
                size="small"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  'data-testid': `wo-create-checklist-petugas-${group}`,
                }}
              />
            )}
          />
          <Autocomplete
            options={picOptions}
            value={checklistDraft.disahkan_oleh_manajer_teknis}
            onChange={(_, row) => setChecklistDraft((prev) => ({ ...prev, disahkan_oleh_manajer_teknis: row }))}
            loading={usersQ.isPending}
            getOptionLabel={userOptionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Disahkan oleh manajer teknis" size="small" fullWidth />
            )}
          />
          <TextField
            label="Jumlah wadah contoh uji air"
            size="small"
            value={checklistDraft.jumlah_wadah_contoh_uji_air}
            onChange={(ev) =>
              setChecklistDraft((prev) => ({ ...prev, jumlah_wadah_contoh_uji_air: ev.target.value }))
            }
            fullWidth
            inputProps={{ 'data-testid': `wo-create-checklist-wadah-air-${group}` }}
          />
          <TextField
            label="Jumlah wadah contoh uji padatan"
            size="small"
            value={checklistDraft.jumlah_wadah_contoh_uji_padatan}
            onChange={(ev) =>
              setChecklistDraft((prev) => ({ ...prev, jumlah_wadah_contoh_uji_padatan: ev.target.value }))
            }
            fullWidth
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <PageShell
      fill
      title="Buat work order"
      subtitle={`Dari quotation: ${q.quotation_number}`}
      extras={
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => goTab(v)}
          sx={PAGE_SHELL_SCROLLABLE_TABS_SX}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="Detail" value="detail" data-testid="wo-create-tab-detail" />
          <Tab
            label="Daftar Sample"
            value="sampling_plan"
            data-testid="wo-create-tab-sampling-plan"
          />
        </Tabs>
      }
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <CollapsibleKeteranganSplit
          toggleTestId="work-order-create-info"
          keterangan={
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Nomor job{' '}
                <strong>{q.company_branch?.job_code?.trim() || 'GL'}-#####</strong>{' '}
                {scheduleId
                  ? 'terbit setelah admin sampling menugaskan petugas lalu menyetujui tim survey.'
                  : 'dibuat otomatis dari kode cabang saat menyimpan.'}
              </Typography>
              {createMut.isError ? (
                <Alert severity="error">
                  {createMut.error instanceof Error
                    ? createMut.error.message
                    : 'Gagal menyimpan work order.'}
                </Alert>
              ) : null}
              {formValidationError ? (
                <Alert
                  severity="error"
                  onClose={() => setFormValidationError(null)}
                  data-testid="work-order-form-validation-error"
                >
                  {formValidationError}
                </Alert>
              ) : null}
              {sampleSubmitError ? (
                <Alert severity="error">{sampleSubmitError}</Alert>
              ) : null}
              {submitBlockedHint ? (
                <Typography variant="caption" color="text.secondary">
                  {submitBlockedHint}
                </Typography>
              ) : null}
            </Stack>
          }
        >
          <Box sx={{ display: tab === 'detail' ? 'block' : 'none' }} aria-hidden={tab !== 'detail'}>
            <Stack spacing={2} sx={{ maxWidth: 560 }}>
              <TextField
                label="Tanggal work order"
                type="date"
                value={workOrderDate}
                onChange={(ev) => setWorkOrderDate(ev.target.value)}
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'work-order-form-date' }}
              />
              <WorkOrderStatusTooltip status={status}>
                <TextField
                  select
                  label="Status"
                  value={status}
                  onChange={(ev) => setStatus(ev.target.value as (typeof statuses)[number])}
                  required
                  fullWidth
                  size="small"
                  SelectProps={{
                    SelectDisplayProps: {
                      id: 'work-order-form-status-trigger',
                    },
                  }}
                >
                  {statuses.map((s) => (
                    <MenuItem key={s} value={s}>
                      <WorkOrderStatusMenuItemLabel status={s} />
                    </MenuItem>
                  ))}
                </TextField>
              </WorkOrderStatusTooltip>
              <TextField
                select
                label="Bulan (angka Romawi)"
                value={romanQuarter}
                onChange={(ev) => setRomanQuarter(ev.target.value as RomanMonth)}
                required
                fullWidth
                size="small"
                helperText="Default mengikuti bulan kalender saat ini (I = Januari … XII = Desember)."
                SelectProps={{
                  SelectDisplayProps: {
                    id: 'work-order-form-roman-quarter-trigger',
                  },
                }}
              >
                {romanOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tahun kalender"
                type="number"
                value={calendarYear}
                onChange={(ev) => {
                  const n = Number.parseInt(ev.target.value, 10);
                  setCalendarYear(Number.isFinite(n) ? n : defaultYear);
                }}
                required
                fullWidth
                size="small"
                inputProps={{ min: 1900, max: 2100, 'data-testid': 'work-order-form-calendar-year' }}
                helperText={`Default tahun berjalan (${defaultYear}).`}
              />
              <TextField
                label="Mulai pengujian"
                type="datetime-local"
                value={testingStart}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'work-order-form-testing-start' }}
                helperText={WORK_ORDER_TESTING_DATES_HELPER}
              />
              <TextField
                label="Selesai pengujian"
                type="datetime-local"
                value={testingEnd}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'work-order-form-testing-end' }}
              />
              <Typography variant="subtitle2" sx={{ pt: 1 }}>
                Ringkasan quotation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pelanggan: <strong>{q.customer?.name ?? '—'}</strong> · Cabang:{' '}
                <strong>{formatCompanyBranchLabel(q.company_branch)}</strong> · Status:{' '}
                <strong>{quotationStatusLabel(q.progress_status)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {scheduleId
                  ? `Nomor job (${q.company_branch?.job_code?.trim() || 'GL'}-#####) terbit setelah admin sampling menyetujui tim survey.`
                  : `Nomor job (${q.company_branch?.job_code?.trim() || 'GL'}-#####) mengikuti kode cabang quotation dan dibuat otomatis saat menyimpan work order draft.`}
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{ display: tab === 'sampling_plan' ? 'block' : 'none' }}
            aria-hidden={tab !== 'sampling_plan'}
          >
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                Daftar jenis pengukuran pada quotation. Checklist rencana pengambilan contoh diisi
                kemudian oleh petugas sampling yang ditugaskan.
              </Typography>
              {renderSamplingPlanTable(quotationSampleLines)}
            </Stack>
          </Box>
        </CollapsibleKeteranganSplit>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={createMut.isPending}
            data-testid="work-order-form-submit"
          >
            {createMut.isPending ? 'Menyimpan…' : 'Simpan work order (draft)'}
          </Button>
          <Button component={RouterLink} to={`/quotation/${encodeURIComponent(documentId)}/view`}>
            Batal
          </Button>
          <Button component={RouterLink} to="/quotations">
            Ke daftar quotation
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}
